import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { digObject, launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as campaignQuoteTemplateSectionActions from '@redux/modules/campaignQuoteTemplateSection'

import defaultFormState, { requiredFields } from '@models/campaignQuoteTemplateSection'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type {
  CampaignQuoteTemplateSectionModel,
  CampaignQuoteTemplateSectionRequestOptions,
} from '@models/types'

import useCampaignQuoteTemplateItems from './useCampaignQuoteTemplateItems'

export const generateUrls = (campaignQuoteTemplateSection?: Partial<CampaignQuoteTemplateSectionModel>) => {
  const { id } = campaignQuoteTemplateSection || {}

  return {
    editCampaignQuoteTemplateSectionDetailsUrl: `#/systemManager/campaignQuoteTemplateSections/${id}/edit/details`,
    editCampaignQuoteTemplateSectionUrl: `#/systemManager/campaignQuoteTemplateSections/${id}/edit/`,
    campaignQuoteTemplateSectionsIndexUrl: '#/systemManager/campaignQuoteTemplateSections',
  }
}

type CreateCampaignQuoteTemplateSectionParams = {
  campaignQuoteTemplateSectionParams: Partial<CampaignQuoteTemplateSectionModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteTemplateSectionRequestOptions,
}

const createCampaignQuoteTemplateSection = (params: CreateCampaignQuoteTemplateSectionParams) => {
  const { dispatch, campaignQuoteTemplateSectionParams, requestOptions } = params
  const { createCampaignQuoteTemplateSection: createFn } = campaignQuoteTemplateSectionActions

  return dispatch(createFn(campaignQuoteTemplateSectionParams, requestOptions))
}

type UpdateCampaignQuoteTemplateSectionParams = {
  campaignQuoteTemplateSection: CampaignQuoteTemplateSectionModel,
  campaignQuoteTemplateSectionParams: Partial<CampaignQuoteTemplateSectionModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteTemplateSectionRequestOptions,
}

const updateCampaignQuoteTemplateSection = (params: UpdateCampaignQuoteTemplateSectionParams) => {
  const {
    dispatch, campaignQuoteTemplateSection, campaignQuoteTemplateSectionParams, requestOptions,
  } = params
  const { updateCampaignQuoteTemplateSection: updateFn } = campaignQuoteTemplateSectionActions

  const updatedParams = {
    id: campaignQuoteTemplateSection.id,
    ...campaignQuoteTemplateSectionParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type UpdateQuoteTemplateSectionOrderParams = {
  campaignQuoteTemplateId: number,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteTemplateSectionRequestOptions,
  sortedQuoteTemplateSectionIds: number[],
}

const updateQuoteTemplateSectionOrder = (params: UpdateQuoteTemplateSectionOrderParams) => {
  const {
    campaignQuoteTemplateId, dispatch, requestOptions, sortedQuoteTemplateSectionIds,
  } = params
  const { updateQuoteTemplateSectionOrder: updateFn } = campaignQuoteTemplateSectionActions

  return dispatch(updateFn(campaignQuoteTemplateId, sortedQuoteTemplateSectionIds, requestOptions))
}

type MoveQuoteTemplateSectionIdParams = {
  campaignQuoteTemplateId: number,
  campaignQuoteTemplateSectionId: number,
  campaignQuoteTemplateSectionIds: number[],
  dispatch: AppDispatch,
  fromIndex: number,
  requestOptions?: CampaignQuoteTemplateSectionRequestOptions,
  toIndex: number,
}

const moveQuoteTemplateSectionId = (params: MoveQuoteTemplateSectionIdParams) => {
  const {
    campaignQuoteTemplateId,
    campaignQuoteTemplateSectionId,
    campaignQuoteTemplateSectionIds,
    dispatch,
    fromIndex,
    requestOptions,
    toIndex,
  } = params

  const updatedSectionIds = [...campaignQuoteTemplateSectionIds]

  // If we are creating a new section we need to add it to the array first
  // so we can sort it correctly
  const currentSectionIndex = updatedSectionIds.findIndex(id => id === campaignQuoteTemplateSectionId)
  if (currentSectionIndex === -1) updatedSectionIds.push(campaignQuoteTemplateSectionId)

  updatedSectionIds.splice(toIndex, 0, updatedSectionIds.splice(fromIndex, 1)[0])

  return updateQuoteTemplateSectionOrder({
    campaignQuoteTemplateId,
    dispatch,
    requestOptions,
    sortedQuoteTemplateSectionIds: updatedSectionIds,
  })
}

type AddSectionAtIndexParams = {
  campaignQuoteTemplateId: number,
  campaignQuoteTemplateSectionIds: number[],
  currentIndex: number,
  dispatch: AppDispatch,
  lastIndex: number,
  requestOptions?: CampaignQuoteTemplateSectionRequestOptions,
}

const addSectionAtIndex = async (params: AddSectionAtIndexParams) => {
  const {
    campaignQuoteTemplateId,
    campaignQuoteTemplateSectionIds,
    currentIndex,
    dispatch,
    lastIndex,
    requestOptions,
  } = params

  // Create Section
  const createResult = await createCampaignQuoteTemplateSection({
    campaignQuoteTemplateSectionParams: {
      campaign_quote_template_id: campaignQuoteTemplateId,
      title: 'Marketing',
    },
    dispatch,
    requestOptions,
  })

  // Return if failed
  if (!createResult.success) return createResult

  // Move in to position
  return moveQuoteTemplateSectionId({
    campaignQuoteTemplateId,
    campaignQuoteTemplateSectionId: createResult.data.id,
    campaignQuoteTemplateSectionIds,
    dispatch,
    fromIndex: lastIndex + 1,
    requestOptions,
    toIndex: currentIndex + 1,
  })
}

type DeleteCampaignQuoteTemplateSectionParams = {
  campaignQuoteTemplateSection: DeleteParams<CampaignQuoteTemplateSectionModel>,
  dispatch: AppDispatch,
}

const deleteCampaignQuoteTemplateSection = (params: DeleteCampaignQuoteTemplateSectionParams) => {
  const { dispatch, campaignQuoteTemplateSection } = params
  const { deleteCampaignQuoteTemplateSection: deleteFn } = campaignQuoteTemplateSectionActions

  return dispatch(deleteFn(campaignQuoteTemplateSection))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCampaignQuoteTemplateSectionForm(
  campaignQuoteTemplateSection: Partial<CampaignQuoteTemplateSectionModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignQuoteTemplateSectionForm = useForm(
    defaultFormState,
    { entity: campaignQuoteTemplateSection, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [campaignQuoteTemplateSection.id, campaignQuoteTemplateSection.cache_key],
  )

  return {
    ...campaignQuoteTemplateSectionForm,
  }
}

export const useRelations = (campaignQuoteTemplateSection: Partial<CampaignQuoteTemplateSectionModel> = {}) => {
  const { campaign_quote_template_id } = campaignQuoteTemplateSection

  const { campaignQuoteTemplates } = useSelector(reduxState => reduxState.entities)

  const campaignQuoteTemplate = campaign_quote_template_id && campaignQuoteTemplates[campaign_quote_template_id]
    ? campaignQuoteTemplates[campaign_quote_template_id]
    : {}

  return {
    campaignQuoteTemplate,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: CampaignQuoteTemplateSectionRequestOptions,
}

function useCampaignQuoteTemplateSection(
  initEntity: Partial<CampaignQuoteTemplateSectionModel> = {},
  options: Options = {},
) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: campaignQuoteTemplateSection } : { entity: CampaignQuoteTemplateSectionModel } = useLatestEntity(
    // @ts-expect-error TS2345
    initEntity,
    'campaignQuoteTemplateSections',
  )
  const { id } = campaignQuoteTemplateSection

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { campaignQuoteTemplate } = useRelations(campaignQuoteTemplateSection)

  useReduxAction(
    'campaignQuoteTemplateSections',
    'loadCampaignQuoteTemplateSection',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.campaignQuoteTemplateSections)

  // Items
  const campaignQuoteTemplateItemsPayload = useCampaignQuoteTemplateItems({
    filters: {
      campaign_quote_template_section_id: id,
    },
    performHttpRequests: false,
  })
  const { filteredCampaignQuoteTemplateItems } = campaignQuoteTemplateItemsPayload

  const subtotal = filteredCampaignQuoteTemplateItems
    .filter((item => item.selected))
    .reduce((acc, item) => acc + parseFloat(digObject(item, 'price_override', 0)), 0)

  const hideDates = digObject(campaignQuoteTemplateSection, 'options.hide_dates', false)
  const hidePrices = digObject(campaignQuoteTemplateSection, 'options.hide_prices', false)
  const showSubtotal = digObject(campaignQuoteTemplateSection, 'options.show_subtotal', true)

  return {
    callbacks: {
      addCustomItem: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignQuoteTemplateItemModal',
        payload: {
          campaignQuoteTemplateItem: {
            // @ts-expect-error TS2339
            campaign_quote_template_id: campaignQuoteTemplate.id,
            campaign_quote_template_section_id: campaignQuoteTemplateSection.id,
            item_type: 'custom',
          },
        },
      }),
      addOrderableItem: () => launchModal({
        callbacks,
        modalKey: 'NewOrderModal',
        payload: {
          // @ts-expect-error TS2339
          agencyId: campaignQuoteTemplate.agency_id,
          // @ts-expect-error TS2339
          campaignId: campaignQuoteTemplate.campaign_id,
          // @ts-expect-error TS2339
          campaignQuoteTemplateId: campaignQuoteTemplate.id,
          quoteSectionId: campaignQuoteTemplateSection.id,
        },
      }),
      addSectionAtIndex: (
        params: { campaignQuoteTemplateSectionIds: number[], index: number, lastIndex: number },
        entityOptions?: CampaignQuoteTemplateSectionRequestOptions,
      ) => {
        const { campaignQuoteTemplateSectionIds, index, lastIndex } = params

        return addSectionAtIndex({
          // @ts-expect-error TS2339
          campaignQuoteTemplateId: campaignQuoteTemplate.id,
          campaignQuoteTemplateSectionIds,
          currentIndex: index,
          dispatch,
          lastIndex,
          requestOptions: entityOptions,
        })
      },
      createCampaignQuoteTemplateSection: (
        campaignQuoteTemplateSectionParams: Partial<CampaignQuoteTemplateSectionModel>,
        entityOptions?: CampaignQuoteTemplateSectionRequestOptions,
      ) => (
        createCampaignQuoteTemplateSection({
          campaignQuoteTemplateSectionParams,
          dispatch,
          requestOptions: entityOptions,
        })
      ),
      createOrEditCampaignQuoteTemplateSection: (customPayload?: object) => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignQuoteTemplateSectionModal',
        payload: { ...customPayload, campaignQuoteTemplateSection },
      }),
      deleteCampaignQuoteTemplateSection: () => (
        deleteCampaignQuoteTemplateSection({ dispatch, campaignQuoteTemplateSection })
      ),
      moveQuoteTemplateSectionId: (
        params: { campaignQuoteTemplateSectionIds: number[], fromIndex: number, toIndex: number },
        entityOptions?: CampaignQuoteTemplateSectionRequestOptions,
      ) => {
        const { campaignQuoteTemplateSectionIds, fromIndex, toIndex } = params

        return moveQuoteTemplateSectionId({
          // @ts-expect-error TS2339
          campaignQuoteTemplateId: campaignQuoteTemplate.id,
          campaignQuoteTemplateSectionId: campaignQuoteTemplateSection.id,
          campaignQuoteTemplateSectionIds,
          dispatch,
          fromIndex,
          requestOptions: entityOptions,
          toIndex,
        })
      },
      updateCampaignQuoteTemplateSection: (
        campaignQuoteTemplateSectionParams: Partial<CampaignQuoteTemplateSectionModel>,
        entityOptions?: CampaignQuoteTemplateSectionRequestOptions,
      ) => (
        updateCampaignQuoteTemplateSection({
          campaignQuoteTemplateSection, campaignQuoteTemplateSectionParams, dispatch, requestOptions: entityOptions,
        })
      ),
      updateQuoteTemplateSectionOrder: (
        params: { campaignQuoteTemplateId: number, sortedQuoteTemplateSectionIds: number[] },
        entityOptions?: CampaignQuoteTemplateSectionRequestOptions,
      ) => {
        const { campaignQuoteTemplateId, sortedQuoteTemplateSectionIds } = params

        return updateQuoteTemplateSectionOrder({
          campaignQuoteTemplateId,
          sortedQuoteTemplateSectionIds,
          dispatch,
          requestOptions: entityOptions,
        })
      },
    },
    campaignQuoteTemplate,
    campaignQuoteTemplateSection,
    creating,
    deleting,
    hideDates,
    hidePrices,
    loading: loading && !loadedIds.includes(campaignQuoteTemplateSection.id),
    showSubtotal,
    subtotal,
    updating,
    urls: generateUrls(campaignQuoteTemplateSection),
  }
}

export default useCampaignQuoteTemplateSection
